import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Users, Target, Layers, ArrowRight, Check, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO = "'IBM Plex Mono', monospace";

const PERKS = [
  { icon: Users, title: 'Verified talent gallery', text: 'Browse pre-vetted designers, creatives and craftspeople across the world’s fashion capitals.' },
  { icon: Target, title: 'AI role-fit & Matchmaker', text: 'Rank candidates by fit for your exact role — no more endless scrolling.' },
  { icon: Layers, title: 'Pipeline & Talent Briefs', text: 'Move shortlisted talent through a visual hiring pipeline and post standing briefs.' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (['recon', 'intelligence'].includes(user.subscription_plan)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-[calc(100vh-72px)] bg-white px-6 py-16 lg:py-20" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 border border-[hsl(var(--primary)/0.4)] px-3 py-1 mb-7"
        >
          <ShieldCheck className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
          <span className="text-[hsl(var(--primary))]" style={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.18em' }}>
            ACCOUNT VERIFIED &amp; ACTIVE
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
          className="text-4xl sm:text-5xl font-semibold text-foreground tracking-tight mb-4"
          style={{ fontFamily: SERIF }}
        >
          Welcome to FashionSpyder, {firstName}.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
          className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-12 max-w-2xl"
        >
          You're all set. One last step before you can scout the industry's most sought-after talent —
          choose the plan that fits your hiring needs. Cancel anytime.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border border border-border mb-12">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 + i * 0.07 }}
              className="bg-white p-6"
            >
              <p.icon className="w-5 h-5 text-[hsl(var(--primary))] mb-4" />
              <h3 className="text-base font-semibold text-foreground mb-1.5" style={{ fontFamily: SERIF }}>{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.42 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Button
            onClick={() => navigate('/pricing?welcome=1')}
            className="rounded-none bg-foreground text-background hover:bg-foreground/85 gap-2 px-8 h-12 text-base"
            data-testid="onboarding-choose-plan-btn"
          >
            <Sparkles className="w-4 h-4" /> Choose your plan
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/gallery')}
            className="rounded-none border-border hover:border-foreground gap-2 px-8 h-12 text-base"
            data-testid="onboarding-explore-btn"
          >
            Take a look first <ArrowRight className="w-4 h-4" />
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground" style={{ fontFamily: MONO }}
        >
          {['Cancel anytime', 'Secure payments by Stripe', 'For fashion brand hiring teams'].map((t) => (
            <span key={t} className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> {t}</span>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Onboarding;
